/* eslint-disable no-console */
import { useState, useEffect, useCallback } from "react";
import toast from "react-hot-toast";
import { Modal, ModalHeader, ModalBody, ModalFooter, Button } from "reactstrap";
import { useAuthContext } from "../../context/auth";
import {
  vincularFacturaEmitida,
  ignorarFacturaEmitida,
  reactivarFactura,
  desvincularVinculacion,
  getVinculaciones,
} from "../../utils/conciliacionSII";
import { convertirAMonedaLocal, loadExchangeRates } from "../../utils/exchangeRateService";
import { client } from "../../utils/supabase";

// ─── Helpers ─────────────────────────────────────────────

const formatMonto = (monto) => {
  if (monto == null) return "-";
  return `$ ${Number(monto).toLocaleString("es-CL", { maximumFractionDigits: 0 })}`;
};

const formatRut = (rut) => {
  if (!rut) return "-";
  const [cuerpo, dv] = rut.split("-");
  if (!dv) return rut;
  return `${Number(cuerpo).toLocaleString("es-CL")}-${dv}`;
};

const formatFecha = (fechaStr) => {
  if (!fechaStr) return "-";
  if (fechaStr.includes("T")) fechaStr = fechaStr.substring(0, 10);
  const [year, month, day] = fechaStr.split("-");
  return `${day}/${month}/${year}`;
};

const TIPO_LABEL = {
  sales: "Publicidad",
  sales_btl: "BTL",
  sales_contratos: "Contrato",
  negocios_carteleria: "Cartelería",
};

const thStyle = {
  padding: "6px 8px",
  textAlign: "center",
  color: "#999",
  fontSize: "0.65rem",
  textTransform: "uppercase",
};

const matchTipo = (filtro, r) => {
  if (filtro === "publicidad") return r.tipo === "sales" || r.tipo === "sales_contratos";
  if (filtro === "hardware") return r.tipo === "negocios_carteleria" && r.subtipo !== "Gestión";
  if (filtro === "gestion") return r.tipo === "negocios_carteleria" && r.subtipo === "Gestión";
  return r.tipo === filtro;
};

// ─── Buscar oportunidades del mismo mes ──────────────────

const buscarOportunidades = async (dte, pais) => {
  await loadExchangeRates();
  const resultados = [];
  const folio = String(dte.folio);

  // Derivar mes/año del DTE para filtrar por período
  const fechaDTE = dte.fecha_emision?.substring(0, 10) || "";
  const [anoDTE, mesDTE] = fechaDTE.split("-");
  const mesDesde = `${anoDTE}-${mesDTE}-01`;
  const lastDay = new Date(Number(anoDTE), Number(mesDTE), 0).getDate();
  const mesHasta = `${anoDTE}-${mesDTE}-${String(lastDay).padStart(2, "0")}`;

  // 1. Sales del mismo mes
  try {
    const { data: salesMes } = await client
      .from("sales")
      .select("id, cliente, client_id, importe, facturanumero, facturafecha, plazocobro, estatus, moneda_dolar")
      .eq("pais", pais)
      .eq("eliminada", false)
      .gte("facturafecha", mesDesde)
      .lte("facturafecha", mesHasta);

    (salesMes || []).forEach((s) =>
      resultados.push({
        tipo: "sales",
        id: s.id,
        cliente: s.cliente,
        client_id: s.client_id,
        monto: s.importe,
        facturanumero: s.facturanumero,
        fecha: s.facturafecha,
        plazocobro: s.plazocobro,
        estatus: s.estatus,
        moneda_dolar: s.moneda_dolar || false,
        match_razon: s.facturanumero === folio ? "folio_exacto" : "monto_similar",
      })
    );
  } catch (err) {
    console.error("Error buscando sales:", err);
  }

  // 2. Sales BTL del mismo mes
  try {
    const { data: btlMes } = await client
      .from("sales_btl")
      .select("id, client_id, total_investment, facturanumero, facturafecha, facturafechacobro, status, moneda_dolar")
      .eq("pais", pais)
      .eq("eliminada", false)
      .gte("facturafecha", mesDesde)
      .lte("facturafecha", mesHasta);

    (btlMes || []).forEach((s) =>
      resultados.push({
        tipo: "sales_btl",
        id: s.id,
        cliente: null,
        client_id: s.client_id,
        monto: s.total_investment,
        facturanumero: s.facturanumero,
        fecha: s.facturafecha,
        plazocobro: s.facturafechacobro,
        estatus: s.status,
        moneda_dolar: s.moneda_dolar || false,
        match_razon: s.facturanumero === folio ? "folio_exacto" : "monto_similar",
      })
    );
  } catch (err) {
    console.error("Error buscando sales_btl:", err);
  }

  // 3. Sales Contratos: buscar folio en meses[]
  try {
    const { data: contratos } = await client
      .from("sales_contratos")
      .select("id, cliente, client_id, importe_total, meses, estatus, moneda_dolar")
      .eq("pais", pais)
      .eq("eliminada", false);

    (contratos || []).forEach((c) => {
      if (!Array.isArray(c.meses)) return;
      c.meses.forEach((mes, idx) => {
        const mesFecha = mes.facturafecha?.substring(0, 7);
        if (mesFecha === `${anoDTE}-${mesDTE}` || mes.facturanumero === folio) {
          resultados.push({
            tipo: "sales_contratos",
            id: c.id,
            ref_index: idx,
            cliente: c.cliente,
            client_id: c.client_id,
            monto: mes.importe || c.importe_total,
            facturanumero: mes.facturanumero,
            fecha: mes.facturafecha,
            plazocobro: mes.plazocobro || null,
            estatus: c.estatus,
            moneda_dolar: c.moneda_dolar || false,
            match_razon: mes.facturanumero === folio ? "folio_exacto" : "monto_similar",
          });
        }
      });
    });
  } catch (err) {
    console.error("Error buscando sales_contratos:", err);
  }

  // 4. Negocios Cartelería: buscar en meses_facturacion
  try {
    const [{ data: carteleria }, { data: cliData }, { data: edifData }] = await Promise.all([
      client
        .from("negocios_carteleria")
        .select(
          "id, nombre_proyecto, referencia_id, tipo_vertical, monto_hardware, meses_facturacion, detalle_pagos_hardware, estado, incluye_contenidos, modalidad, incluye_hardware, cantidad_pantallas, fee_gestion_unitario, fee_contenidos_unitario, cuota_hardware_opex, moneda_gestion, moneda_contenidos, moneda_hardware"
        )
        .eq("pais", pais)
        .eq("eliminada", false),
      client.from("clientes").select("id, nombre, empresa").eq("pais", pais),
      client.from("edificios").select("id, edificio").eq("pais", pais),
    ]);

    const cliMap = {};
    (cliData || []).forEach((c) => {
      cliMap[c.id] = c;
    });
    const edifMap = {};
    (edifData || []).forEach((e) => {
      edifMap[e.id] = e;
    });

    (carteleria || []).forEach((c) => {
      let nombreRef = `ID ${c.referencia_id}`;
      if (c.tipo_vertical === "TD" && cliMap[c.referencia_id]) {
        nombreRef = cliMap[c.referencia_id].empresa || cliMap[c.referencia_id].nombre;
      } else if (c.tipo_vertical === "EO" && edifMap[c.referencia_id]) {
        nombreRef = edifMap[c.referencia_id].edificio;
      }
      const nombre = c.nombre_proyecto || nombreRef;
      const pantallas = Number(c.cantidad_pantallas) || 1;

      // meses_facturacion: cada entrada genera conceptos separados según tipo
      if (Array.isArray(c.meses_facturacion)) {
        c.meses_facturacion.forEach((mes, idx) => {
          const mesFecha = mes.facturafecha?.substring(0, 7);
          if (mesFecha !== `${anoDTE}-${mesDTE}` && mes.facturanumero !== folio) return;
          const matchR = mes.facturanumero === folio ? "folio_exacto" : "monto_similar";
          const fechaRef = mes.facturafecha || null;
          const defaultMoneda = pais === "Chile" ? "UF" : "PEN";

          if (mes.tipo === "opex" && c.incluye_hardware && c.modalidad === "OPEX") {
            const cuotaBase = (Number(c.cuota_hardware_opex) || 0) * pantallas;
            const monto = convertirAMonedaLocal(cuotaBase, c.moneda_hardware || defaultMoneda, pais, fechaRef);
            resultados.push({
              tipo: "negocios_carteleria",
              subtipo: "HW-OPEX",
              id: c.id,
              ref_index: idx,
              cliente: nombre,
              client_id: null,
              monto,
              facturanumero: mes.facturanumero,
              fecha: mes.facturafecha,
              plazocobro: null,
              estatus: c.estado,
              match_razon: matchR,
            });
          }

          if (mes.tipo === "gestion") {
            const feeG = (Number(c.fee_gestion_unitario) || 0) * pantallas;
            const feeC = c.incluye_contenidos ? (Number(c.fee_contenidos_unitario) || 0) * pantallas : 0;
            const monedaG = c.moneda_gestion || defaultMoneda;
            const monedaC = c.moneda_contenidos || monedaG;
            const gestionLocal = convertirAMonedaLocal(feeG, monedaG, pais, fechaRef);
            const contenidoLocal = convertirAMonedaLocal(feeC, monedaC, pais, fechaRef);
            resultados.push({
              tipo: "negocios_carteleria",
              subtipo: "Gestión",
              id: c.id,
              ref_index: idx,
              cliente: nombre,
              client_id: null,
              monto: gestionLocal + contenidoLocal,
              facturanumero: mes.facturanumero,
              fecha: mes.facturafecha,
              plazocobro: null,
              estatus: c.estado,
              match_razon: matchR,
            });
          }
        });
      }

      // detalle_pagos_hardware: HW-CAPEX
      const detalle = Array.isArray(c.detalle_pagos_hardware) ? c.detalle_pagos_hardware : [];
      detalle.forEach((pago, idx) => {
        const pagoFecha = pago.fecha_factura?.substring(0, 7) || pago.facturafecha?.substring(0, 7);
        const pagoFolio = pago.facturanumero || "";
        if (pagoFecha !== `${anoDTE}-${mesDTE}` && pagoFolio !== folio) return;
        resultados.push({
          tipo: "negocios_carteleria",
          subtipo: "HW-CAPEX",
          id: c.id,
          ref_index: `hw-${idx}`,
          cliente: nombre,
          client_id: null,
          monto: pago.monto || c.monto_hardware,
          facturanumero: pagoFolio,
          fecha: pago.fecha_factura || pago.facturafecha,
          plazocobro: null,
          estatus: c.estado,
          match_razon: pagoFolio === folio ? "folio_exacto" : "monto_similar",
        });
      });
    });
  } catch (err) {
    console.error("Error buscando negocios_carteleria:", err);
  }

  // Deduplicar por tipo+id+ref_index
  const seen = new Set();
  const unicos = resultados.filter((r) => {
    const key = `${r.tipo}-${r.id}-${r.ref_index ?? ""}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });

  // Ordenar: folio_exacto primero, luego por folio ascendente dentro de cada grupo
  unicos.sort((a, b) => {
    if (a.match_razon === "folio_exacto" && b.match_razon !== "folio_exacto") return -1;
    if (a.match_razon !== "folio_exacto" && b.match_razon === "folio_exacto") return 1;
    const folioA = Number(a.facturanumero) || 0;
    const folioB = Number(b.facturanumero) || 0;
    return folioA - folioB;
  });

  return unicos;
};

// ─── Componente Modal ────────────────────────────────────

const VincularEmitidaModal = ({ isOpen, toggle, factura, onVinculada }) => {
  const { pais, id: userId } = useAuthContext();
  const [resultados, setResultados] = useState([]);
  const [vinculaciones, setVinculaciones] = useState([]);
  const [buscando, setBuscando] = useState(false);
  const [vinculando, setVinculando] = useState(false);
  const [plazocobro, setPlazocobro] = useState("");
  const [confirmando, setConfirmando] = useState(null);
  const [filtroTipo, setFiltroTipo] = useState("");

  // Cargar vinculaciones existentes de esta factura
  const cargarVinculaciones = useCallback(async () => {
    if (!factura?.id) return;
    const vincs = await getVinculaciones(factura.id);
    setVinculaciones(vincs);
  }, [factura?.id]);

  // ¿Esta venta ya está vinculada a esta factura SII?
  const esVinculada = (r) =>
    vinculaciones.some(
      (v) =>
        v.venta_tipo === r.tipo &&
        String(v.venta_ref_id) === String(r.id) &&
        String(v.venta_ref_index ?? "") === String(r.ref_index ?? "")
    );

  const handleDesvincular = async (resultado) => {
    setVinculando(true);
    try {
      await desvincularVinculacion(factura.id, {
        venta_tipo: resultado.tipo,
        venta_ref_id: resultado.id,
        venta_ref_index: resultado.ref_index ?? null,
      });
      toast.success("Vinculación eliminada");
      await cargarVinculaciones();
      onVinculada?.();
    } catch (err) {
      console.error("Error desvinculando:", err);
      toast.error("Error al desvincular");
    } finally {
      setVinculando(false);
    }
  };

  const buscar = useCallback(async () => {
    if (!factura || !pais) return;
    setBuscando(true);
    try {
      const res = await buscarOportunidades(factura, pais);
      setResultados(res);
    } catch (err) {
      console.error("Error buscando oportunidades:", err);
      toast.error("Error buscando oportunidades");
    } finally {
      setBuscando(false);
    }
  }, [factura, pais]);

  useEffect(() => {
    if (isOpen && factura) {
      setResultados([]);
      setVinculaciones([]);
      setPlazocobro("");
      setConfirmando(null);
      setFiltroTipo("");
      buscar();
      cargarVinculaciones();
    }
  }, [isOpen, factura, buscar, cargarVinculaciones]);

  // Pre-cargar plazocobro al seleccionar un resultado
  const handleSelectForPlazocobro = (resultado) => {
    if (resultado.plazocobro) setPlazocobro(resultado.plazocobro);
  };

  const handleVincular = async (resultado) => {
    setVinculando(true);
    try {
      // Vincular en facturas_sii_emitidas
      await vincularFacturaEmitida(factura.id, {
        venta_tipo: resultado.tipo,
        venta_ref_id: resultado.id,
        venta_ref_index: resultado.ref_index ?? null,
        cliente_id: resultado.client_id,
        usuario_id: userId,
      });

      // Actualizar plazocobro en la tabla de origen si se ingresó
      if (plazocobro) {
        try {
          if (resultado.tipo === "sales") {
            await client.from("sales").update({ plazocobro }).eq("id", resultado.id);
          } else if (resultado.tipo === "sales_btl") {
            await client.from("sales_btl").update({ plazocobro }).eq("id", resultado.id);
          } else if (resultado.tipo === "sales_contratos" && resultado.ref_index != null) {
            const { data } = await client.from("sales_contratos").select("meses").eq("id", resultado.id).single();
            if (data?.meses) {
              const meses = [...data.meses];
              meses[resultado.ref_index] = { ...meses[resultado.ref_index], plazocobro };
              await client.from("sales_contratos").update({ meses }).eq("id", resultado.id);
            }
          }
        } catch (err) {
          console.error("Error actualizando plazocobro:", err);
        }
      }

      // Detectar concepto hermano en cartelería (mismo negocio, mismo mes)
      if (resultado.tipo === "negocios_carteleria" && resultado.ref_index != null) {
        const hermanos = resultados.filter(
          (r) =>
            r.tipo === "negocios_carteleria" &&
            String(r.id) === String(resultado.id) &&
            String(r.ref_index) !== String(resultado.ref_index) &&
            !esVinculada(r)
        );
        for (const hermano of hermanos) {
          const label = hermano.subtipo || "otro concepto";
          const confirmar = window.confirm(
            `Esta factura también cubre ${label} de ${hermano.cliente}.\n¿Vincular también?`
          );
          if (confirmar) {
            await vincularFacturaEmitida(factura.id, {
              venta_tipo: hermano.tipo,
              venta_ref_id: hermano.id,
              venta_ref_index: hermano.ref_index ?? null,
              cliente_id: hermano.client_id,
              usuario_id: userId,
            });
          }
        }
      }

      toast.success("Factura vinculada correctamente");
      await cargarVinculaciones();
      onVinculada?.();
      toggle();
    } catch (err) {
      console.error("Error vinculando:", err);
      toast.error("Error al vincular factura");
    } finally {
      setVinculando(false);
    }
  };

  const handleDescartar = async () => {
    setVinculando(true);
    try {
      await ignorarFacturaEmitida(factura.id, userId);
      toast.success("DTE descartado");
      onVinculada?.();
      toggle();
    } catch (err) {
      console.error("Error descartando:", err);
      toast.error("Error al descartar DTE");
    } finally {
      setVinculando(false);
    }
  };

  const handleReactivar = async () => {
    setVinculando(true);
    try {
      await reactivarFactura(factura.id, "emitida");
      toast.success("Factura reactivada");
      onVinculada?.();
      toggle();
    } catch (err) {
      console.error("Error reactivando:", err);
      toast.error("Error al reactivar factura");
    } finally {
      setVinculando(false);
    }
  };

  if (!factura) return null;

  const esIgnorada = factura.estado_vinculacion === "ignorada";

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="xl" backdropClassName="modal-backdrop-dark">
      <ModalHeader toggle={toggle}>Vincular Factura Emitida — Folio {factura.folio}</ModalHeader>
      <ModalBody>
        {/* Banner ignorada */}
        {esIgnorada && (
          <div
            style={{
              background: "#fff3cd",
              color: "#856404",
              padding: "10px 14px",
              borderRadius: 6,
              marginBottom: 14,
              fontSize: "0.82rem",
              fontWeight: 600,
            }}
          >
            Esta factura está marcada como descartada
          </div>
        )}

        {/* Detalle del DTE — una línea */}
        <div
          style={{
            background: "#f8f9fa",
            padding: "10px 16px",
            borderRadius: 6,
            marginBottom: 14,
            fontSize: "0.82rem",
            display: "flex",
            flexWrap: "wrap",
            gap: "4px 24px",
            alignItems: "baseline",
            justifyContent: "center",
          }}
        >
          <span>
            <strong>Fecha:</strong> {formatFecha(factura.fecha_emision)}
          </span>
          <span>
            <strong>Cliente:</strong> {factura.razon_social_receptor}
          </span>
          <span>
            <strong>RUT:</strong> {formatRut(factura.rut_receptor)}
          </span>
          <span>
            <strong>Neto:</strong> {formatMonto(factura.monto_neto)}
          </span>
          <span>
            <strong>IVA:</strong> {formatMonto(factura.monto_iva)}
          </span>
          <span>
            <strong>Total:</strong> {formatMonto(factura.monto_total)}
          </span>
        </div>

        {/* Filtro de tipo */}
        {!esIgnorada && !confirmando && !buscando && resultados.length > 0 && (
          <div
            style={{
              display: "flex",
              gap: 0,
              marginBottom: 10,
              borderRadius: 5,
              overflow: "hidden",
              border: "1px solid #dee2e6",
            }}
          >
            {[
              { value: "", label: "Todos" },
              { value: "publicidad", label: "Publicidad" },
              { value: "hardware", label: "Hardware" },
              { value: "gestion", label: "Gestión" },
              { value: "sales_btl", label: "BTL" },
            ].map((f) => (
              <button
                key={f.value}
                onClick={() => setFiltroTipo(f.value)}
                style={{
                  padding: "4px 14px",
                  fontSize: "0.72rem",
                  fontWeight: 600,
                  border: "none",
                  cursor: "pointer",
                  background: filtroTipo === f.value ? "#2c3e50" : "transparent",
                  color: filtroTipo === f.value ? "#fff" : "#666",
                }}
              >
                {f.label}
              </button>
            ))}
          </div>
        )}

        {/* Resultados */}
        {!esIgnorada && !confirmando && buscando && (
          <div style={{ textAlign: "center", padding: 20, color: "#999" }}>Buscando oportunidades...</div>
        )}

        {!esIgnorada && !confirmando && !buscando && resultados.length === 0 && (
          <div style={{ textAlign: "center", padding: 20, color: "#999" }}>
            No se encontraron oportunidades para este período.
          </div>
        )}

        {!esIgnorada && !confirmando && !buscando && resultados.length > 0 && (
          <table style={{ width: "100%", fontSize: "0.78rem", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ borderBottom: "2px solid #dee2e6" }}>
                <th style={thStyle}>Tipo</th>
                <th style={thStyle}>Cliente</th>
                <th style={thStyle}>Monto</th>
                <th style={thStyle}>Folio</th>
                <th style={thStyle}>Fecha</th>
                <th style={thStyle}>Match</th>
                <th style={thStyle}>Acción</th>
              </tr>
            </thead>
            <tbody>
              {(filtroTipo ? resultados.filter((r) => matchTipo(filtroTipo, r)) : resultados).map((r, i) => (
                <tr
                  key={`${r.tipo}-${r.id}-${i}`}
                  style={{ borderBottom: "1px solid #f0f0f0" }}
                  onMouseEnter={() => handleSelectForPlazocobro(r)}
                >
                  <td style={{ padding: "8px 12px", whiteSpace: "nowrap", textAlign: "center" }}>
                    <span
                      style={{
                        fontSize: "0.65rem",
                        fontWeight: 600,
                        padding: "2px 6px",
                        borderRadius: 4,
                        background: "#e8f4fd",
                        color: "#2980b9",
                      }}
                    >
                      {r.subtipo || TIPO_LABEL[r.tipo] || r.tipo}
                    </span>
                  </td>
                  <td style={{ padding: "8px 12px", textAlign: "center" }}>{r.cliente || "-"}</td>
                  <td
                    style={{
                      padding: "8px 12px",
                      textAlign: "right",
                      fontVariantNumeric: "tabular-nums",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {r.moneda_dolar ? `US$ ${Number(r.monto || 0).toLocaleString("es-CL")}` : formatMonto(r.monto)}
                  </td>
                  <td style={{ padding: "8px 12px", textAlign: "center" }}>{r.facturanumero || "-"}</td>
                  <td style={{ padding: "8px 12px", textAlign: "center", whiteSpace: "nowrap" }}>
                    {formatFecha(r.fecha)}
                  </td>
                  <td style={{ padding: "8px 12px", textAlign: "center", whiteSpace: "nowrap" }}>
                    {esVinculada(r) ? (
                      <span
                        style={{
                          fontSize: "0.6rem",
                          fontWeight: 600,
                          padding: "1px 5px",
                          borderRadius: 4,
                          minWidth: 70,
                          display: "inline-block",
                          textAlign: "center",
                          background: "#d5f5e3",
                          color: "#27ae60",
                        }}
                      >
                        Vinculada ✓
                      </span>
                    ) : (
                      <span
                        style={{
                          fontSize: "0.6rem",
                          fontWeight: 600,
                          padding: "1px 5px",
                          borderRadius: 4,
                          minWidth: 70,
                          display: "inline-block",
                          textAlign: "center",
                          background: r.match_razon === "folio_exacto" ? "#d5f5e3" : "#ffecd2",
                          color: r.match_razon === "folio_exacto" ? "#27ae60" : "#e67e22",
                        }}
                      >
                        {r.match_razon === "folio_exacto" ? "Folio exacto" : "Monto similar"}
                      </span>
                    )}
                  </td>
                  <td style={{ padding: "8px 12px", textAlign: "center", whiteSpace: "nowrap" }}>
                    {esVinculada(r) ? (
                      <button
                        onClick={() => handleDesvincular(r)}
                        disabled={vinculando}
                        style={{
                          fontSize: "0.72rem",
                          padding: "3px 0",
                          width: 90,
                          textAlign: "center",
                          border: "1px solid #c0392b",
                          borderRadius: 4,
                          background: "transparent",
                          color: "#c0392b",
                          cursor: "pointer",
                          fontWeight: 600,
                        }}
                      >
                        Desvincular
                      </button>
                    ) : (
                      <button
                        onClick={() => {
                          handleSelectForPlazocobro(r);
                          setConfirmando(r);
                        }}
                        style={{
                          fontSize: "0.72rem",
                          padding: "3px 0",
                          width: 90,
                          textAlign: "center",
                          border: "1px solid #2c3e50",
                          borderRadius: 4,
                          background: "#2c3e50",
                          color: "#fff",
                          cursor: "pointer",
                          fontWeight: 600,
                        }}
                      >
                        Vincular
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {/* Panel de confirmación */}
        {!esIgnorada && confirmando && (
          <div
            style={{
              marginTop: 16,
              padding: "14px 16px",
              background: "#eaf2f8",
              borderRadius: 6,
              border: "1px solid #aed6f1",
              fontSize: "0.82rem",
            }}
          >
            <div style={{ fontWeight: 700, marginBottom: 8 }}>Confirmar vinculación</div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: "6px 28px" }}>
              <div>
                <strong>DTE Folio:</strong> {factura.folio} — {formatMonto(factura.monto_total)}
              </div>
              <div>
                <strong>Oportunidad:</strong> {TIPO_LABEL[confirmando.tipo] || confirmando.tipo} —{" "}
                {confirmando.cliente || "Sin nombre"} — {formatMonto(confirmando.monto)}
              </div>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 10 }}>
              <label style={{ fontWeight: 600, whiteSpace: "nowrap", fontSize: "0.78rem" }}>Cobro estimado:</label>
              <input
                type="date"
                value={plazocobro}
                onChange={(e) => setPlazocobro(e.target.value)}
                style={{
                  height: 30,
                  fontSize: "0.78rem",
                  padding: "4px 8px",
                  border: "1px solid #aed6f1",
                  borderRadius: 4,
                }}
              />
            </div>
            <div style={{ display: "flex", gap: 8, marginTop: 10, justifyContent: "flex-end" }}>
              <Button color="secondary" outline size="sm" onClick={() => setConfirmando(null)}>
                Volver
              </Button>
              <Button color="dark" size="sm" onClick={() => handleVincular(confirmando)} disabled={vinculando}>
                {vinculando ? "Vinculando..." : "Confirmar"}
              </Button>
            </div>
          </div>
        )}
      </ModalBody>
      <ModalFooter>
        {esIgnorada ? (
          <>
            <Button color="secondary" size="sm" onClick={toggle}>
              Cerrar
            </Button>
            <Button color="warning" size="sm" onClick={handleReactivar} disabled={vinculando}>
              {vinculando ? "..." : "Reactivar"}
            </Button>
          </>
        ) : confirmando ? (
          <Button color="secondary" size="sm" onClick={toggle}>
            Cerrar
          </Button>
        ) : (
          <>
            <Button color="secondary" outline size="sm" onClick={handleDescartar} disabled={vinculando}>
              Descartar
            </Button>
            <Button color="secondary" size="sm" onClick={toggle}>
              Cerrar
            </Button>
          </>
        )}
      </ModalFooter>
    </Modal>
  );
};

export default VincularEmitidaModal;
